import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { PreferencesProvider } from './contexts/PreferencesContext';
import HomeIcon from '@mui/icons-material/Home';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HistoryIcon from '@mui/icons-material/History';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import './styles.css';

// Components
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ConfirmSignUp from './components/auth/ConfirmSignUp';
import Home from './components/Home';
import Camera from './components/Camera';
import MealAnalysis from './components/MealAnalysis';
import EditMealAnalysis from './components/EditMealAnalysis';
import MealHistory from './components/MealHistory';
import NotFound from './components/NotFound';
import UserPreferences from './components/UserPreferences';
import MenuAnalysis from './components/MenuAnalysis';

const AppContent = () => {
  const location = useLocation();
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const user = await getCurrentUser();
        setUserEmail(user.signInDetails?.loginId || '');
      } catch (error) {
        console.error('Error fetching user email:', error);
      }
    };
    fetchUserEmail();
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <img src="/images/00168-3555891285.png" alt="FoodTracker AI Logo" className="app-logo" />
          <h1 className="app-title">FoodTracker-AI</h1>
        </div>
        <button 
          className="settings-button"
          onClick={() => setShowSettings(!showSettings)}
        >
          <SettingsIcon />
        </button>
        {showSettings && (
          <div className="settings-menu">
            <div className="settings-menu-email">
              {userEmail}
            </div>
            <div 
              className="settings-menu-item"
              onClick={() => {
                setShowSettings(false);
                setShowPreferences(true);
              }}
            >
              <PersonIcon />
              <span>Preferences</span>
            </div>
            <div className="settings-menu-item" onClick={() => signOut()}>
              <LogoutIcon />
              <span>Sign Out</span>
            </div>
          </div>
        )}
      </header>

      <UserPreferences 
        open={showPreferences} 
        onClose={() => setShowPreferences(false)} 
      />

      <main className="container">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/confirm-signup" element={<ConfirmSignUp />} />
          <Route path="/" element={<Home />} />
          <Route path="/camera" element={<Camera />} />
          <Route path="/analysis" element={<MealAnalysis />} />
          <Route path="/edit-meal" element={<EditMealAnalysis />} />
          <Route path="/history" element={<MealHistory />} />
          <Route path="/menu-analysis" element={<MenuAnalysis />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <nav className="bottom-nav">
        <Link to="/" className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
          <HomeIcon className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link to="/camera" className={`nav-item ${location.pathname === '/camera' ? 'active' : ''}`}>
          <CameraAltIcon className="nav-icon" />
          <span>Capture</span>
        </Link>
        <Link to="/history" className={`nav-item ${location.pathname === '/history' ? 'active' : ''}`}>
          <HistoryIcon className="nav-icon" />
          <span>History</span>
        </Link>
        <Link to="/menu-analysis" className={`nav-item ${location.pathname === '/menu-analysis' ? 'active' : ''}`}>
          <RestaurantMenuIcon className="nav-icon" />
          <span>Menu</span>
        </Link>
      </nav>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuthState();

    // Listen for auth events
    const listener = Hub.listen('auth', (data) => {
      const { payload } = data;
      
      switch (payload.event) {
        case 'signedIn':
          setIsAuthenticated(true);
          setLoading(false);
          break;
        case 'tokenRefresh':
          // Token refresh succeeded, ensure we stay authenticated
          setIsAuthenticated(true);
          setLoading(false);
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
        case 'signInWithRedirect_failure':
          // Handle sign out and authentication failures by redirecting to login
          setIsAuthenticated(false);
          setLoading(false);
          break;
      }
    });

    return () => {
      // Cleanup listener
      listener();
    };
  }, []);

  const checkAuthState = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
      setLoading(false);
    } catch (error) {
      console.log('User is not authenticated:', error);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <PreferencesProvider>
      <Router>
      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      ) : isAuthenticated ? (
        <AppContent />
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
      </Router>
    </PreferencesProvider>
  );
}

export default App;
